import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, User, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { useBooking } from "../context/BookingContext";
import { useAuth } from "../context/AuthContext";

const nav = [
  { label: "Services", to: "/services", children: [
    { to: "/services", label: "Services" },
    { to: "/quiz", label: "Treatment Finder" },
    { to: "/gallery", label: "Results" },
  ] },
  { label: "Membership", to: "/membership" },
  { label: "Rewards", to: "/rewards" },
  { label: "Financing", to: "/financing" },
  { label: "Team", to: "/team" },
  { label: "Shop", to: "/shop", children: [
    { to: "/shop", label: "Shop" },
    { to: "/gift-cards", label: "Gift Cards" },
  ] },
  { label: "Contact", to: "/contact", children: [
    { to: "/contact", label: "Contact" },
    { to: "/blog", label: "Journal" },
  ] },
];

export function Navbar({ bannerVisible = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openBooking } = useBooking();
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 z-50 transition-all duration-500 ${
        bannerVisible ? "top-10" : "top-0"
      } ${scrolled ? "glass-nav py-3" : "py-5 bg-transparent"}`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" className="flex items-center group" data-testid="logo-link">
          <img
            src={`${process.env.PUBLIC_URL}/logo-transparent.png`}
            alt="Iridescent Medical Spa"
            className="h-9 w-auto transition-transform duration-500 group-hover:scale-105"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {nav.map((item) =>
            item.children ? (
              <div key={item.label} className="relative group" data-testid={`nav-group-${item.label.toLowerCase()}`}>
                <NavLink
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1 text-sm tracking-wide transition-colors hover:text-ink focus:text-ink outline-none ${
                      isActive ? "text-ink font-medium" : "text-[#5C6664]"
                    }`
                  }
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
                </NavLink>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0">
                  <div className="bg-white rounded-xl border border-slate-100 shadow-xl shadow-blue-900/10 p-2 min-w-[170px]">
                    {item.children.map((c) => (
                      <NavLink
                        key={c.to + c.label}
                        to={c.to}
                        data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) =>
                          `block px-3 py-2 rounded-lg text-sm transition-colors hover:bg-stone ${
                            isActive ? "text-ink font-medium bg-stone" : "text-[#5C6664]"
                          }`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors hover:text-ink ${
                    isActive ? "text-ink font-medium" : "text-[#5C6664]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <Link to="/dashboard" data-testid="nav-dashboard-link">
              <Button variant="ghost" className="rounded-full text-ink gap-2">
                <User className="h-4 w-4" /> {user.name?.split(" ")[0] || "Account"}
              </Button>
            </Link>
          ) : (
            <Link to="/login" data-testid="nav-login-link">
              <Button variant="ghost" className="rounded-full text-ink">
                Member Login
              </Button>
            </Link>
          )}
          <Button
            data-testid="nav-book-now-button"
            onClick={() => openBooking()}
            className="iri-gradient text-ink font-medium rounded-full px-6 hover:scale-105 transition-transform"
          >
            Book Now
          </Button>
        </div>

        <button
          className="lg:hidden text-ink p-1"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden glass-nav mt-3 mx-4 rounded-2xl p-6 space-y-4" data-testid="mobile-menu">
          {nav.map((item) =>
            item.children ? (
              <div key={item.label} className="space-y-2">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#8A9492]">{item.label}</p>
                {item.children.map((c) => (
                  <NavLink
                    key={c.to + c.label}
                    to={c.to}
                    className="block text-base text-ink pl-3"
                    data-testid={`mobile-nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {c.label}
                  </NavLink>
                ))}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className="block text-base text-ink"
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </NavLink>
            )
          )}
          <div className="pt-3 border-t border-black/5 flex flex-col gap-3">
            <Link to={user ? "/dashboard" : "/login"} className="text-ink text-base">
              {user ? "My Account" : "Member Login"}
            </Link>
            <Button
              onClick={() => openBooking()}
              className="iri-gradient text-ink font-medium rounded-full w-full"
              data-testid="mobile-book-now-button"
            >
              Book Now
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
